/**
 * Planning box (ao_planning_box_profile_and_reserved_slots_first_2026_09_05.md R20
 * [UI] P0): the main/planning host's roster — main + review + the 7
 * reserved-function slots + general backlog workers — rendered as ONE
 * clearly-labelled section, every slot tagged by function, visually distinct
 * from the per-node groups the Fleet table renders below it. Node hosts are
 * untouched by this component entirely — it only ever reads the central host's
 * own slots (`buildPlanningBoxRoster`'s own `isCentral`-mirroring filter).
 *
 * Purely presentational — takes `slots` from whichever page already fetched
 * `/api/state` (no independent fetch of its own), same as `AgentsPanel`/`Fleet`.
 * Display logic lives in pure, exported mappers (layout.tsx), vitest-covered in
 * PlanningBox.test.ts — mirrors the FleetKpis.tsx/HumanFleet.tsx recipe.
 */

import { Panel } from "./components";
import {
  buildPlanningBoxRoster,
  PLANNING_BOX_FUNCTION_LABEL,
  planningBoxCapacity,
  resolveCentralHostId,
  type PlanningBoxFunction,
} from "./layout";
import type { SlotView } from "./types";

const _MUTE = "var(--fg-2)";

export function planningBoxCapacityLine(capacity: { general: number; reserved: number }): string {
  const generalPart = `${capacity.general} general slot${capacity.general === 1 ? "" : "s"} free for backlog dispatch`;
  if (capacity.reserved === 0) return generalPart;
  return `${generalPart} · ${capacity.reserved} reserved (main/review/CI escalation/scheduled)`;
}

export function PlanningBox({ slots }: { slots: SlotView[] }) {
  const centralHostId = resolveCentralHostId(slots);
  const rows = buildPlanningBoxRoster(slots, centralHostId);
  // A bare fixture with no roster at all (e.g. a unit test seeding only node
  // slots) renders nothing rather than an empty shell — same "don't show a
  // panel with nothing in it" convention HumanFleet.tsx's own Panel-vs-Empty
  // branch follows.
  if (rows.length === 0) return null;
  const capacity = planningBoxCapacity(rows);
  return (
    <div className="planning-box" data-testid="planning-box">
      <Panel
        title="Planning box"
        tone="fleet"
        flush
        right={
          <span className="planning-box-section-profile mono" data-testid="planning-box-section-profile">
            profile: planning
          </span>
        }
      >
        <div className="planning-box-capacity" data-testid="planning-box-capacity">
          {planningBoxCapacityLine(capacity)}
        </div>
        <table className="table planning-box-table">
          <thead>
            <tr>
              <th>Slot</th>
              <th>Function</th>
              <th>Status</th>
              <th>Current task</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              // Composite (host, slot_id) key, never bare slotId — slot_id COLLIDES across
              // hosts BY DESIGN (see layout.tsx's slotKey docstring); a central row and a
              // node row can legitimately share the same number (confirmed live by this
              // component's own e2e spec against seed_e2e_state.py's E2E_COLLISION_SLOT_ID).
              <tr key={r.key} data-testid={`planning-box-row-${r.key}`}>
                <td className="mono">#{r.slotId}</td>
                <td>
                  <FunctionBadge fn={r.fn} />
                </td>
                <td className="mono">{r.status}</td>
                <td className="mono" style={{ color: r.currentTask ? undefined : _MUTE }}>
                  {r.currentTask ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function FunctionBadge({ fn }: { fn: PlanningBoxFunction }) {
  return (
    <span className={`planning-box-fn-badge fn-${fn}`} data-testid={`planning-box-fn-${fn}`}>
      {PLANNING_BOX_FUNCTION_LABEL[fn]}
    </span>
  );
}
